// Per-incident chat hook — streams the backend's Vercel AI SDK UI message stream
// protocol (text-delta + data-status + data-tool parts) over the dashboard's
// Bearer-auth fetch, tracking tool calls and the live (streaming) message.

import { useCallback, useEffect, useRef, useState } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { getSessionCookieToken } from '../api/client'

const API_BASE = import.meta.env.VITE_API_URL || ''

export type ChatRole = 'user' | 'assistant'

export type ToolCall = { name: string; detail?: string }

export type ChatMessage = {
  id: string
  role: ChatRole
  content: string
  tools?: ToolCall[]
}

type HistoryResponse = { messages: { id: string; role: string; content: string }[] }

let _localId = 0
function localId(prefix: string): string {
  _localId += 1
  return `${prefix}-${_localId}`
}

export function useIncidentChat(investigationId: string | undefined) {
  const { getToken } = useAuth()
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [status, setStatus] = useState<string | null>(null)
  const [streaming, setStreaming] = useState(false)
  const [streamingId, setStreamingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)

  const authToken = useCallback(async (): Promise<string | null> => {
    return getSessionCookieToken() ?? (await getToken())
  }, [getToken])

  // Load persisted transcript + pre-warm the sandbox when the panel mounts.
  useEffect(() => {
    if (!investigationId) return
    let cancelled = false
    void (async () => {
      const token = await authToken()
      if (!token) return
      try {
        const res = await fetch(
          `${API_BASE}/api/v1/investigations/${investigationId}/chat/messages`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        if (res.ok && !cancelled) {
          const body = (await res.json()) as HistoryResponse
          setMessages(
            body.messages
              .filter((m) => m.role === 'user' || m.role === 'assistant')
              .map((m) => ({ id: m.id, role: m.role as ChatRole, content: m.content })),
          )
        }
      } catch {
        /* history is best-effort */
      }
      void fetch(`${API_BASE}/api/v1/investigations/${investigationId}/chat/prepare`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      }).catch(() => {})
    })()
    return () => {
      cancelled = true
    }
  }, [investigationId, authToken])

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim()
      if (!investigationId || !trimmed || streaming) return
      setError(null)
      setStreaming(true)
      setStatus('resolving')

      const userMsg: ChatMessage = { id: localId('u'), role: 'user', content: trimmed }
      const assistantId = localId('a')
      setStreamingId(assistantId)
      setMessages((prev) => [
        ...prev,
        userMsg,
        { id: assistantId, role: 'assistant', content: '', tools: [] },
      ])

      const appendAssistant = (delta: string) => {
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantId ? { ...m, content: m.content + delta } : m)),
        )
      }
      const addTool = (tool: ToolCall) => {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId ? { ...m, tools: [...(m.tools ?? []), tool] } : m,
          ),
        )
      }

      try {
        const token = await authToken()
        if (!token) throw new Error('Not authenticated')
        const controller = new AbortController()
        abortRef.current = controller
        const res = await fetch(`${API_BASE}/api/v1/investigations/${investigationId}/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ message: trimmed }),
          signal: controller.signal,
        })
        if (!res.ok || !res.body) throw new Error(`Chat request failed (${res.status})`)

        const reader = res.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        for (;;) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const frames = buffer.split('\n\n')
          buffer = frames.pop() ?? ''
          for (const frame of frames) {
            const line = frame.split('\n').find((l) => l.startsWith('data:'))
            if (!line) continue
            const payload = line.slice('data:'.length).trim()
            if (!payload || payload === '[DONE]') continue
            handlePart(payload, { appendAssistant, addTool, setStatus, setError })
          }
        }
      } catch (err) {
        if ((err as Error)?.name !== 'AbortError') {
          setError(err instanceof Error ? err.message : 'Chat failed')
        }
      } finally {
        setStreaming(false)
        setStatus(null)
        setStreamingId(null)
        abortRef.current = null
      }
    },
    [investigationId, streaming, authToken],
  )

  const stop = useCallback(() => {
    abortRef.current?.abort()
  }, [])

  return { messages, status, streaming, streamingId, error, send, stop }
}

type PartHandlers = {
  appendAssistant: (delta: string) => void
  addTool: (tool: ToolCall) => void
  setStatus: (value: string | null) => void
  setError: (value: string | null) => void
}

function handlePart(payload: string, h: PartHandlers): void {
  let part: {
    type?: string
    delta?: string
    errorText?: string
    data?: { value?: string; name?: string; detail?: string }
  }
  try {
    part = JSON.parse(payload)
  } catch {
    return
  }
  switch (part.type) {
    case 'text-delta':
      if (part.delta) h.appendAssistant(part.delta)
      break
    case 'data-status':
      h.setStatus(part.data?.value ?? null)
      break
    case 'data-tool':
      if (part.data?.name) h.addTool({ name: part.data.name, detail: part.data.detail })
      break
    case 'error':
      h.setError(part.errorText ?? 'Chat error')
      break
    default:
      // start / text-start / text-end / finish — no-op
      break
  }
}
